import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { PUT, loading, alert } from "../../components/CustomComponent";
import NicknameCell from "./NicknameCell";
import BonusCell, { BonusMaxCell } from "./BonusCell";
import ColumnPicker, { useHiddenColumns } from "./ColumnPicker";
import useStickyColumns from "./useStickyColumns";

const API = "v1/cmru/class-sections";

// เครื่องที่ตั้ง "ลดการเคลื่อนไหว": bootstrap ตัด transition ของ modal ทิ้ง ทำให้บางจังหวะ react-bootstrap ไม่ได้รับสัญญาณว่าปิดเสร็จ
// modal จะค้างแบบมองไม่เห็นและบังการกดทั้งหน้า จึงปิดแอนิเมชันไปเลย (ถอด modal ออกทันที) ซึ่งตรงกับความต้องการของผู้ใช้กลุ่มนี้อยู่แล้ว
const modalAnimation = !(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

// ลำดับการวนเมื่อกดช่อง: ว่าง -> มา -> สาย -> ลา -> ขาด -> ว่าง
const ATTENDANCE = {
  present: { letter: "ม", label: "มา" },
  late: { letter: "ส", label: "สาย" },
  leave: { letter: "ล", label: "ลา" },
  absent: { letter: "ข", label: "ขาด" },
};
const CYCLE = ["", "present", "late", "leave", "absent"];

// แต้มของแต่ละสถานะและเกณฑ์หมดสิทธิ์สอบ ตั้งเองได้ต่อกลุ่มเรียน (ค่าด้านล่างคือค่าเริ่มต้นของระบบ)
export const DEFAULT_POINTS = { present: 0, late: 0.5, leave: 1, absent: 1 };
const DEFAULT_THRESHOLD = 4.5;

const ENROLLMENT_STATUS = {
  normal: { label: "ปกติ", className: "stg-pill--success", icon: "fa-solid fa-circle-check" },
  no_exam: { label: "หมดสิทธิ์สอบ", className: "stg-pill--danger", icon: "fa-solid fa-ban" },
  withdrawn: { label: "ถอนรายวิชา", className: "stg-pill--muted", icon: "fa-solid fa-right-from-bracket" },
  resigned: { label: "ลาออก", className: "stg-pill--muted", icon: "fa-solid fa-user-xmark" },
};
// สถานะที่ไม่อยู่ในห้องแล้ว (ถอน/ลาออก): ข้ามตอนเติม "มา" ทั้งห้อง แถวเป็นสีเทา และไม่จัดอันดับในแท็บคะแนน
export const INACTIVE_STATUSES = ["withdrawn", "resigned"];

// คะแนนเข้าเรียนถึงครึ่งของเกณฑ์หมดสิทธิ์สอบ = "ใกล้หมดสิทธิ์" ขึ้นสีเหลืองเตือนไว้ก่อน
const WARN_RATIO = 0.5;

// อัตรามาเรียน: ดี / พอใช้ / น่าห่วง ใช้กับแถวสรุปท้ายตาราง
const getRateTone = (rate) => (rate >= 80 ? "good" : rate >= 60 ? "warn" : "bad");

// คอลัมน์ที่เลือกซ่อนได้ (เลขที่กับชื่อ-สกุลแสดงเสมอ)
const COLUMN_OPTIONS = [
  { key: "code", label: "รหัส (นศ.)" },
  { key: "nick", label: "ชื่อเล่น" },
  { key: "bonus", label: "โบนัส" },
  { key: "weeks", label: "สัปดาห์ (ช่องเช็คชื่อ)" },
  { key: "score", label: "คะแนนเข้าเรียน" },
  { key: "status", label: "สถานะ" },
];

const weeksCount = (detail) => detail.total_weeks || 16;

const getStudentName = (student) => [student?.prefix_name, [student?.first_name, student?.last_name].filter(Boolean).join(" ")].filter(Boolean).join("") || "-";

const countStatuses = (attendance = {}) =>
  Object.values(attendance).reduce((acc, status) => ({ ...acc, [status]: (acc[status] || 0) + 1 }), { present: 0, late: 0, leave: 0, absent: 0 });

// คะแนนเข้าเรียน = ผลรวมแต้มของทุกสัปดาห์ที่เช็คชื่อแล้ว (คิดเป็นจำนวนเต็มของ 1/100 กันทศนิยมเพี้ยน เช่น 0.1 + 0.2)
export const getAttendanceScore = (attendance = {}, points = DEFAULT_POINTS) =>
  Object.values(attendance).reduce((sum, status) => sum + Math.round(Number(points[status] ?? 0) * 100), 0) / 100;

// สถานะ: ผู้สอนเลือกทับไว้ = ใช้ค่านั้น ไม่งั้นคะแนนเข้าเรียน >= เกณฑ์ของวิชา = หมดสิทธิ์สอบ (ไม่ตั้งเกณฑ์ = ปกติเสมอ)
export const resolveEnrollmentStatus = (enrollment, points, threshold) => {
  if (enrollment.status_override) return { key: enrollment.status_override, isOverride: true };
  const hasThreshold = threshold !== null && threshold !== undefined;
  const exceeded = hasThreshold && getAttendanceScore(enrollment.attendance, points) >= Number(threshold);
  return { key: exceeded ? "no_exam" : "normal", isOverride: false };
};

const formatScore = (value) => (Number.isInteger(value * 10) ? value.toFixed(1) : String(value));

const ModalHeader = ({ eyebrow, title, subtitle }) => (
  <Modal.Header closeButton>
    <div>
      <p className="stg-eyebrow">{eyebrow}</p>
      <Modal.Title>{title}</Modal.Title>
      {subtitle ? <p className="stg-modal-sub">{subtitle}</p> : null}
    </div>
  </Modal.Header>
);

const SettingsModal = ({ show, onHide, detail, onSave }) => {
  const [form, setForm] = useState({ total_weeks: 16, ...DEFAULT_POINTS, no_exam_threshold: DEFAULT_THRESHOLD });

  useEffect(() => {
    setForm({
      total_weeks: detail?.total_weeks ?? 16,
      ...DEFAULT_POINTS,
      ...(detail?.attendance_points || {}),
      no_exam_threshold: detail?.no_exam_threshold ?? "",
    });
  }, [detail, show]);

  const handleInput = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  return (
    <Modal animation={modalAnimation} show={show} onHide={onHide} centered dialogClassName="stg-modal cmru-modal">
      <ModalHeader eyebrow="Attendance" title="ตั้งค่าการเช็คชื่อ" subtitle="ใช้เฉพาะกลุ่มเรียนนี้ แก้ตัวเลขได้ทุกช่อง" />
      <Modal.Body>
        <div className="stg-section">
          <div className="stg-section-head">
            <span className="stg-section-mark"><i className="fa-solid fa-calculator" /></span>
            <div>
              <h4 className="stg-section-title">คะแนนของแต่ละสถานะ</h4>
              <p className="stg-section-desc">คะแนนเข้าเรียนของนักศึกษา = ผลรวมของทุกสัปดาห์ที่เช็คชื่อแล้ว</p>
            </div>
          </div>
          <div className="row g-3">
            {Object.entries(ATTENDANCE).map(([key, item]) => (
              <div className="col-6 col-md-3" key={key}>
                <label className="stg-label d-flex align-items-center gap-2">
                  <span className={`cmru-att-cell is-${key}`} style={{ width: 24, height: 22, fontSize: 12 }}>{item.letter}</span>{item.label}
                </label>
                <input type="number" min="0" step="0.5" name={key} className="form-control" value={form[key]} onChange={handleInput} />
              </div>
            ))}
          </div>
        </div>

        <div className="stg-section">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="stg-label">หมดสิทธิ์สอบ เมื่อคะแนน</label>
              <input type="number" min="0" step="0.5" name="no_exam_threshold" className="form-control" value={form.no_exam_threshold} onChange={handleInput} placeholder="ไม่ตั้งเกณฑ์" />
              <div className="stg-help">คะแนนรวม ≥ ค่านี้ = "หมดสิทธิ์สอบ" ต่ำกว่านี้ = "ปกติ" · เว้นว่าง = ไม่ตั้งเกณฑ์</div>
            </div>
            <div className="col-md-6">
              <label className="stg-label">จำนวนสัปดาห์</label>
              <input type="number" min="1" max="60" name="total_weeks" className="form-control" value={form.total_weeks} onChange={handleInput} />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="stg-modal-actions">
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setForm({ total_weeks: form.total_weeks, ...DEFAULT_POINTS, no_exam_threshold: DEFAULT_THRESHOLD })} title="มา 0 · สาย 0.5 · ลา 1 · ขาด 1 · เกณฑ์ 4.5">
            <i className="fa-solid fa-rotate-left" />ค่าเริ่มต้น
          </button>
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={onHide}>ยกเลิก</button>
          <button type="button" className="btn stg-btn stg-btn--primary" onClick={() => onSave(form)}>
            <i className="fa-solid fa-floppy-disk" />บันทึกข้อมูล
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const StatusModal = ({ show, onHide, enrollment, autoStatusKey, onSelect }) => (
  <Modal animation={modalAnimation} show={show} onHide={onHide} centered size="sm" dialogClassName="stg-modal cmru-modal">
    <ModalHeader eyebrow="Status" title="สถานะนักศึกษา" subtitle={getStudentName(enrollment?.student)} />
    <Modal.Body>
      <div className="d-grid gap-2">
        <button type="button" className={`cmru-status-option ${!enrollment?.status_override ? "is-active" : ""}`} onClick={() => onSelect(null)}>
          <span>
            <strong>อัตโนมัติ</strong>
            <small>คำนวณจากคะแนนเข้าเรียน · ตอนนี้: {ENROLLMENT_STATUS[autoStatusKey]?.label}</small>
          </span>
          {!enrollment?.status_override ? <i className="fa-solid fa-check" /> : null}
        </button>
        {Object.entries(ENROLLMENT_STATUS).map(([key, item]) => (
          <button type="button" key={key} className={`cmru-status-option ${enrollment?.status_override === key ? "is-active" : ""}`} onClick={() => onSelect(key)}>
            <span>
              <strong>{item.label}</strong>
              <small>กำหนดเอง ไม่เปลี่ยนตามคะแนนเข้าเรียน</small>
            </span>
            {enrollment?.status_override === key ? <i className="fa-solid fa-check" /> : null}
          </button>
        ))}
      </div>
    </Modal.Body>
  </Modal>
);

/**
 * แท็บเช็คชื่อของกลุ่มเรียน: ตารางนักศึกษา x สัปดาห์ กดช่องเพื่อวน มา/สาย/ลา/ขาด/ยังไม่เช็ค
 * ไม่บันทึกอัตโนมัติ: การเปลี่ยนแปลงค้างอยู่ใน draft จนกว่าจะกด "บันทึก" (หรือ "ยกเลิก" เพื่อทิ้ง)
 * detail/setDetail/reload มาจากหน้า ClassLists (ใช้ข้อมูลกลุ่มเรียนชุดเดียวกับแท็บรายชื่อ)
 */
export default function AttendanceTab({ detail, setDetail, reload, onDirtyChange }) {
  const [isSaving, setIsSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [search, setSearch] = useState("");
  // คอลัมน์ที่ผู้ใช้เลือกซ่อน จำไว้ในเครื่อง · ซ่อนสัปดาห์แล้วหัวตารางเหลือแถวเดียว
  const columnPick = useHiddenColumns("cmru-att-columns");
  const { show } = columnPick;
  const headRows = show("weeks") ? 2 : 1;
  const visibleCount = 2 + ["code", "nick", "bonus", "score", "status"].filter(show).length + (show("weeks") ? weeksCount(detail) : 0);
  // แถวที่กดเลือกไว้ (is-focus): เด่นค้างไว้และแถวอื่นจางลง เหมือนตอนชี้เมาส์ แต่ไม่หายเมื่อเลื่อนเมาส์ออก
  // กดที่ส่วนว่างของแถวเดิมซ้ำเพื่อยกเลิก ส่วนกดปุ่มในแถว (ช่องเช็ค/สถานะ) จะเลือกแถวนั้นไว้เฉย ๆ ไม่สลับปิด
  const [focusUid, setFocusUid] = useState("");
  const handleRowClick = (event, uid) => {
    if (event.target.closest("button, input, a")) setFocusUid(uid);
    else setFocusUid((current) => (current === uid ? "" : uid));
  };
  const navigate = useNavigate();

  // ตำแหน่งเกาะซ้ายของคอลัมน์ เลขที่/รหัส/ชื่อ/ชื่อเล่น วัดจากความกว้างจริง (ดู useStickyColumns)
  const { tableRef, seqRef, codeRef, nameRef } = useStickyColumns([show("code")]);

  // การเช็คชื่อที่ยังไม่ได้บันทึก: key = "enrollmentUid:week" -> สถานะใหม่ ("" = ยังไม่เช็ค)
  // เก็บเฉพาะช่องที่ต่างจากข้อมูลในระบบ กดวนกลับมาค่าเดิม = ไม่นับว่าเปลี่ยน
  const [draft, setDraft] = useState({});
  // สถานะที่เลือกทับแล้วยังไม่ได้บันทึก: enrollmentUid -> สถานะใหม่ ("" = กลับไปใช้ค่าอัตโนมัติ) รวมอยู่ในปุ่มบันทึกเดียวกัน
  const [statusDraft, setStatusDraft] = useState({});
  const sectionUid = detail.uid;
  const dirtyCount = Object.keys(draft).length + Object.keys(statusDraft).length;
  const isDirty = dirtyCount > 0;

  const discardDraft = () => {
    setDraft({});
    setStatusDraft({});
  };

  const points = { ...DEFAULT_POINTS, ...(detail.attendance_points || {}) };
  const threshold = detail.no_exam_threshold;
  const weeks = Array.from({ length: detail.total_weeks || 16 }, (_, index) => index + 1);

  // รายชื่อที่ใช้แสดงผล = ข้อมูลในระบบ + สิ่งที่แก้ค้างไว้ (คะแนนและสถานะคำนวณจากชุดนี้ จะได้เห็นผลก่อนกดบันทึก)
  const students = (detail.students || []).map((enrollment) => {
    const attendance = { ...(enrollment.attendance || {}) };
    weeks.forEach((weekNo) => {
      const key = `${enrollment.uid}:${weekNo}`;
      if (!(key in draft)) return;
      if (draft[key]) attendance[weekNo] = draft[key];
      else delete attendance[weekNo];
    });
    const status_override = enrollment.uid in statusDraft ? statusDraft[enrollment.uid] || null : enrollment.status_override;
    return { ...enrollment, attendance, status_override };
  });

  const stageChanges = (changes) => {
    const savedByKey = Object.fromEntries((detail.students || []).flatMap((enrollment) => Object.entries(enrollment.attendance || {}).map(([weekNo, status]) => [`${enrollment.uid}:${weekNo}`, status])));

    setDraft((current) => {
      const next = { ...current };
      changes.forEach(({ enrollment_uid, week_no, status }) => {
        const key = `${enrollment_uid}:${week_no}`;
        if ((savedByKey[key] || "") === (status || "")) delete next[key];
        else next[key] = status || "";
      });
      return next;
    });
  };

  const handleCellClick = (enrollment, weekNo) => {
    const current = enrollment.attendance?.[weekNo] || "";
    const next = CYCLE[(CYCLE.indexOf(current) + 1) % CYCLE.length];
    stageChanges([{ enrollment_uid: enrollment.uid, week_no: weekNo, status: next }]);
  };

  const handleSave = async () => {
    if (!isDirty || isSaving) return;

    // ช่องที่ถูกตั้งกลับเป็น "ยังไม่เช็ค" ส่งเป็น status = null เพื่อลบรายการเดิมในระบบ
    const records = Object.entries(draft).map(([key, status]) => {
      const [enrollment_uid, week_no] = key.split(":");
      return { enrollment_uid, week_no: Number(week_no), status: status || null };
    });

    const statuses = Object.entries(statusDraft).map(([enrollment_uid, status]) => ({ enrollment_uid, status_override: status || null }));

    setIsSaving(true);
    loading(true);
    // keepEmpty: ปกติ PUT จะตัด key ที่เป็น null ทิ้งก่อนส่ง แต่ที่นี่ null มีความหมาย (ล้างช่อง / กลับไปใช้สถานะอัตโนมัติ)
    const result = await PUT(`${API}/${sectionUid}/attendance`, { ...(records.length ? { records } : {}), ...(statuses.length ? { statuses } : {}) }, null, { keepEmpty: true });
    if (result?.status === true) {
      await reload();
      discardDraft();
      alert("", "success", "สำเร็จ", `บันทึกแล้ว: เช็คชื่อ ${records.length} ช่อง${statuses.length ? ` · สถานะ ${statuses.length} คน` : ""}`);
    }
    loading(false);
    setIsSaving(false);
  };

  const handleCancel = async () => {
    if (!isDirty) return;
    if (!(await alert("", "warning", "ยกเลิกการเปลี่ยนแปลง", `ทิ้งการเปลี่ยนแปลงที่ยังไม่ได้บันทึก ${dirtyCount} รายการ แล้วกลับไปใช้ข้อมูลล่าสุดในระบบ ?`, "ทิ้งการเปลี่ยนแปลง", "กลับไปแก้ต่อ"))) return;
    discardDraft();
  };

  // แจ้งหน้าหลักว่ามีของค้าง (ใช้ถามยืนยันก่อนสลับแท็บ/กดกลับ)
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirtyChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // กันข้อมูลหายตอนมีของค้าง: ปิด/รีเฟรชหน้า (เบราว์เซอร์ถามเอง) และกดลิงก์เมนูไปหน้าอื่น (ถามยืนยันก่อน)
  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const handleLinkClick = async (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link || link.target === "_blank" || link.origin !== window.location.origin) return;

      event.preventDefault();
      event.stopPropagation();
      if (await alert("", "warning", "ยังไม่ได้บันทึกข้อมูล", `มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก ${dirtyCount} รายการ ออกจากหน้านี้โดยไม่บันทึก ?`, "ออกโดยไม่บันทึก", "อยู่หน้านี้ต่อ")) {
        discardDraft();
        navigate(link.pathname + link.search);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleLinkClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [isDirty, dirtyCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // กดหัวสัปดาห์: เติม "มา" ให้ทุกคนที่ยังว่างในสัปดาห์นั้น (คนที่ถอนรายวิชาข้าม) แล้วค่อยแก้เฉพาะคนที่ลา/ขาด
  const handleFillWeek = async (weekNo) => {
    const targets = students.filter((enrollment) => !enrollment.attendance?.[weekNo] && !INACTIVE_STATUSES.includes(resolveEnrollmentStatus(enrollment, points, threshold).key));
    if (!targets.length) {
      await alert("", "info", `สัปดาห์ที่ ${weekNo}`, "เช็คชื่อครบทุกคนแล้ว");
      return;
    }
    if (!(await alert("", "info", `สัปดาห์ที่ ${weekNo}`, `ตั้ง "มา" ให้ ${targets.length} คนที่ยังไม่ได้เช็คชื่อ ? (แก้รายคนเป็น สาย/ลา/ขาด ได้ภายหลัง)`, "ตั้งเป็นมา", "ยกเลิก"))) return;

    stageChanges(targets.map((enrollment) => ({ enrollment_uid: enrollment.uid, week_no: weekNo, status: "present" })));
  };

  const saveSettings = async (form) => {
    const totalWeeks = Number(form.total_weeks);
    if (!Number.isInteger(totalWeeks) || totalWeeks < 1 || totalWeeks > 60) {
      await alert("", "warning", "แจ้งเตือน", "จำนวนสัปดาห์ต้องเป็นเลข 1-60");
      return;
    }

    const pointValues = Object.keys(ATTENDANCE).map((key) => [key, String(form[key]).trim() === "" ? NaN : Number(form[key])]);
    const invalidPoint = pointValues.find(([, value]) => !Number.isFinite(value) || value < 0);
    if (invalidPoint) {
      await alert("", "warning", "แจ้งเตือน", `กรุณากรอกคะแนนของ "${ATTENDANCE[invalidPoint[0]].label}" เป็นตัวเลขที่ไม่ติดลบ`);
      return;
    }
    const thresholdText = String(form.no_exam_threshold).trim();
    if (thresholdText !== "" && (!Number.isFinite(Number(thresholdText)) || Number(thresholdText) < 0)) {
      await alert("", "warning", "แจ้งเตือน", "เกณฑ์หมดสิทธิ์สอบต้องเป็นตัวเลขที่ไม่ติดลบ หรือเว้นว่าง");
      return;
    }

    loading(true);
    const result = await PUT(`${API}/${sectionUid}/attendance-settings`, {
      total_weeks: totalWeeks,
      ...Object.fromEntries(pointValues.map(([key, value]) => [`point_${key}`, value])),
      no_exam_threshold: thresholdText === "" ? null : Number(thresholdText),
    }, null, { keepEmpty: true });
    loading(false);

    if (result?.status === true) {
      // โหลดข้อมูลใหม่ให้เสร็จก่อนค่อยปิด modal: ถ้าปิดพร้อมกับที่ข้อมูลเปลี่ยน (modal ถูก render ซ้ำกลางแอนิเมชันปิด)
      // บนเครื่องที่ตั้ง "ลดการเคลื่อนไหว" modal จะค้างแบบมองไม่เห็นและบังการกดทั้งหน้า
      await reload();
      setShowSettings(false);
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    }
  };

  // เลือกสถานะ: ยังไม่บันทึกทันที เก็บไว้รวมกับการเช็คชื่อที่ค้างอยู่ แล้วบันทึกพร้อมกันด้วยปุ่ม "บันทึก"
  const stageStatus = (statusOverride) => {
    const target = statusTarget;
    setStatusTarget(null);
    if (!target) return;

    const saved = (detail.students || []).find((enrollment) => enrollment.uid === target.uid)?.status_override || "";
    setStatusDraft((current) => {
      const next = { ...current };
      if (saved === (statusOverride || "")) delete next[target.uid];
      else next[target.uid] = statusOverride || "";
      return next;
    });
  };

  const weekTotals = weeks.map((weekNo) => students.filter((enrollment) => enrollment.attendance?.[weekNo]).length);
  // อัตรามาเรียนของแต่ละสัปดาห์ = (มา + สาย) / คนที่เช็คชื่อแล้ว · สัปดาห์ที่ยังไม่เช็คเลย = null
  const weekRates = weeks.map((weekNo, index) => {
    if (!weekTotals[index]) return null;
    const attended = students.filter((enrollment) => ["present", "late"].includes(enrollment.attendance?.[weekNo])).length;
    return Math.round((attended / weekTotals[index]) * 100);
  });

  const hasThreshold = threshold !== null && threshold !== undefined && Number(threshold) > 0;
  // แถวพร้อมค่าที่คำนวณแล้ว ใช้ทั้งตัวเลขสรุปด้านบนและตัวตาราง
  const rows = students.map((enrollment) => {
    const status = resolveEnrollmentStatus(enrollment, points, threshold);
    const score = getAttendanceScore(enrollment.attendance, points);
    const ratio = hasThreshold ? score / Number(threshold) : 0;
    const level = status.key === "no_exam" && !status.isOverride ? "over" : status.key === "normal" && ratio >= WARN_RATIO ? "warn" : "ok";
    return { enrollment, status, score, ratio, level, counts: countStatuses(enrollment.attendance) };
  });

  // ค้นหากรองเฉพาะแถวที่แสดง ตัวเลขสรุปด้านบนและอัตรามาเรียนท้ายตารางยังคิดจากทั้งห้อง
  const keyword = search.trim().toLowerCase();
  const visibleRows = keyword
    ? rows.filter(({ enrollment }) => `${enrollment.student?.student_code || ""} ${getStudentName(enrollment.student)} ${enrollment.student?.nickname || ""}`.toLowerCase().includes(keyword))
    : rows;

  const panelDesc = `กดช่องเพื่อวน มา → สาย → ลา → ขาด → ว่าง · กดเลขสัปดาห์เพื่อตั้ง "มา" ทั้งห้อง · แก้เสร็จแล้วกดบันทึก${threshold !== null && threshold !== undefined ? ` · คะแนนเข้าเรียน ≥ ${threshold} = หมดสิทธิ์สอบ` : ""}`;

  return (
    <div className="stg-panel">
      <div className="stg-panel-head">
        <div className="stg-panel-intro">
          <span className="stg-panel-icon"><i className="fa-solid fa-clipboard-check" /></span>
          <div style={{ minWidth: 0 }}>
            <p className="stg-eyebrow">Attendance</p>
            <h3 className="stg-panel-title">เช็คชื่อ</h3>
            {/* บรรทัดเดียวเสมอ ยาวเกินตัดเป็น ... ชี้ค้างไว้เพื่ออ่านข้อความเต็ม */}
            <p className="stg-panel-desc cmru-one-line" title={panelDesc}>{panelDesc}</p>
          </div>
        </div>
        <div className="stg-panel-actions">
          <ColumnPicker options={COLUMN_OPTIONS} hidden={columnPick.hidden} onToggle={columnPick.toggle} onReset={columnPick.reset} />
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setShowSettings(true)}>
            <i className="fa-solid fa-sliders" />ตั้งค่า
          </button>
        </div>
      </div>

      <div className="stg-panel-body">
        <div className="cmru-att-legend mb-3">
          {Object.entries(ATTENDANCE).map(([key, item]) => (
            <span key={key}><span className={`cmru-att-cell is-${key}`}>{item.letter}</span>{item.label} = {points[key]}</span>
          ))}
          <span><span className="cmru-att-cell" />ยังไม่เช็ค</span>
        </div>

        <div className="stg-search mb-3">
          <i className="fa-solid fa-magnifying-glass" />
          <input type="text" className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="ค้นหารหัส (นศ.) ชื่อ-สกุล หรือชื่อเล่น..." />
        </div>

        <div className="cmru-att-wrap stg-table-wrap">
          {/* is-no-lock: ซ่อนชื่อเล่นแล้วคอลัมน์ชื่อเป็นตัวสุดท้ายที่เกาะซ้าย (รับเงาขอบขวาแทน) */}
          <table className={`cmru-att-table ${show("nick") ? "" : "is-no-lock"}`} ref={tableRef}>
            <thead>
              <tr>
                <th rowSpan={headRows} className="cmru-att-seq" ref={seqRef}>เลขที่</th>
                {show("code") && <th rowSpan={headRows} className="cmru-att-code" ref={codeRef}>รหัส (นศ.)</th>}
                <th rowSpan={headRows} className="cmru-att-name" ref={nameRef}>ชื่อ-สกุล (นศ.)</th>
                {show("nick") && <th rowSpan={headRows} className="cmru-att-nick cmru-att-lock">ชื่อเล่น</th>}
                {/* ตัวเลขใต้คำว่าโบนัส = คะแนนสูงสุดที่ให้ได้ต่อคน แก้ในช่องได้เลย */}
                {show("bonus") && (
                  <th rowSpan={headRows} className="cmru-att-nick cmru-att-bonus-head">
                    โบนัส
                    <BonusMaxCell
                      sectionUid={sectionUid}
                      max={detail.bonus_max}
                      highest={Math.max(0, ...(detail.students || []).map((row) => Number(row.bonus_score) || 0))}
                      onSaved={(bonus_max) => setDetail((current) => ({ ...current, bonus_max }))}
                    />
                  </th>
                )}
                {show("weeks") && <th colSpan={weeks.length} className="text-center">สัปดาห์</th>}
                {show("score") && <th rowSpan={headRows} className="cmru-att-score">คะแนน<br />เข้าเรียน</th>}
                {show("status") && <th rowSpan={headRows} className="cmru-att-status">สถานะ</th>}
              </tr>
              {show("weeks") && (
                <tr>
                  {weeks.map((weekNo, index) => (
                    <th key={weekNo} className={`cmru-att-week ${weekTotals[index] ? (weekTotals[index] >= students.length ? "is-done" : "is-partial") : ""}`}>
                      <button type="button" onClick={() => handleFillWeek(weekNo)} title={`สัปดาห์ที่ ${weekNo}: เช็คแล้ว ${weekTotals[index]}/${students.length} คน · กดเพื่อตั้ง "มา" ให้คนที่ยังว่าง`}>
                        {weekNo}
                      </button>
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {visibleRows.length > 0 ? (
                visibleRows.map(({ enrollment, status, score, ratio, level, counts }, rowIndex) => {
                  const statusInfo = ENROLLMENT_STATUS[status.key] || ENROLLMENT_STATUS.normal;

                  return (
                    <tr
                      key={enrollment.uid}
                      className={[
                        status.key !== "normal" ? `is-${status.key}` : level === "warn" ? "is-warn" : "",
                        focusUid === enrollment.uid ? "is-focus" : "",
                      ].join(" ").trim() || undefined}
                      onClick={(event) => handleRowClick(event, enrollment.uid)}
                    >
                      <td className="cmru-att-seq"><span className="cmru-seq">{enrollment.seq_no ?? "-"}</span></td>
                      {show("code") && <td className="cmru-att-code">{enrollment.student?.student_code || "-"}</td>}
                      <td className="cmru-att-name">{getStudentName(enrollment.student)}</td>
                      {/* ชื่อเล่นบันทึกทันทีที่กด Enter/คลิกที่อื่น ไม่รวมกับรายการรอบันทึกของการเช็คชื่อ */}
                      {show("nick") && (
                        <td className="cmru-att-nick cmru-att-lock cmru-nick-cell">
                          <NicknameCell
                            sectionUid={sectionUid}
                            enrollment={enrollment}
                            rowIndex={rowIndex}
                            onSaved={(nickname) => setDetail((current) => ({
                              ...current,
                              students: current.students.map((row) => (row.uid === enrollment.uid ? { ...row, student: { ...row.student, nickname } } : row)),
                            }))}
                          />
                        </td>
                      )}
                      {/* โบนัสก็บันทึกทันทีเหมือนชื่อเล่น */}
                      {show("bonus") && (
                        <td className="cmru-att-nick cmru-nick-cell">
                          <BonusCell
                            sectionUid={sectionUid}
                            enrollment={enrollment}
                            max={detail.bonus_max}
                            rowIndex={rowIndex}
                            onSaved={(bonus_score) => setDetail((current) => ({
                              ...current,
                              students: current.students.map((row) => (row.uid === enrollment.uid ? { ...row, bonus_score } : row)),
                            }))}
                          />
                        </td>
                      )}
                      {show("weeks") && weeks.map((weekNo) => {
                        const value = enrollment.attendance?.[weekNo] || "";
                        return (
                          <td key={weekNo} className="cmru-att-week">
                            <button
                              type="button"
                              className={`cmru-att-cell ${value ? `is-${value}` : ""} ${`${enrollment.uid}:${weekNo}` in draft ? "is-dirty" : ""}`}
                              onClick={() => handleCellClick(enrollment, weekNo)}
                              aria-label={`${getStudentName(enrollment.student)} สัปดาห์ที่ ${weekNo}: ${ATTENDANCE[value]?.label || "ยังไม่เช็ค"}`}
                            >
                              {ATTENDANCE[value]?.letter || ""}
                            </button>
                          </td>
                        );
                      })}
                      {/* มาครบ (0 คะแนน) เว้นว่างเหมือนในใบเช็คชื่อเดิม ชี้เมาส์เพื่อดูจำนวนครั้งของแต่ละสถานะ */}
                      {/* แถบใต้ตัวเลข = คะแนนเทียบกับเกณฑ์หมดสิทธิ์สอบ เขียว -> เหลือง (ถึงครึ่ง) -> แดง (ถึงเกณฑ์) */}
                      {show("score") && (
                        <td
                          className={`cmru-att-score is-${level}`}
                          title={`มา ${counts.present} · สาย ${counts.late} · ลา ${counts.leave} · ขาด ${counts.absent}${hasThreshold ? ` · เกณฑ์หมดสิทธิ์สอบ ${threshold}` : ""}`}
                        >
                          {score > 0 ? (
                            <>
                              <span className="cmru-score">{formatScore(score)}</span>
                              {hasThreshold ? <span className="cmru-score-bar"><span style={{ width: `${Math.min(100, ratio * 100)}%` }} /></span> : null}
                            </>
                          ) : ""}
                        </td>
                      )}
                      {show("status") && (
                        <td className="cmru-att-status">
                          <button type="button" className={`stg-pill cmru-status-btn ${statusInfo.className} ${enrollment.uid in statusDraft ? "is-dirty" : ""}`} onClick={() => setStatusTarget(enrollment)} title={status.isOverride ? "กำหนดเอง · กดเพื่อเปลี่ยน" : "คำนวณอัตโนมัติ · กดเพื่อกำหนดเอง"}>
                            <i className={statusInfo.icon} />{statusInfo.label}
                            {status.isOverride ? <i className="fa-solid fa-thumbtack" /> : null}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={visibleCount} className="stg-empty">
                    <i className="fa-regular fa-folder-open" />{rows.length ? "ไม่พบนักศึกษาที่ค้นหา" : "ยังไม่มีรายชื่อในกลุ่มเรียนนี้"}
                  </td>
                </tr>
              )}
            </tbody>
            {/* แถวสรุปเกาะล่างตาราง: อัตรามาเรียนของแต่ละสัปดาห์ เห็นทันทีว่าสัปดาห์ไหนคนหาย */}
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td className="cmru-att-seq" />
                  {show("code") && <td className="cmru-att-code" />}
                  {/* ป้ายอยู่ในคอลัมน์ชื่อเล่น เพราะเป็นคอลัมน์เดียวที่เกาะซ้ายบนทุกขนาดจอ ถ้าซ่อนชื่อเล่นย้ายไปคอลัมน์ชื่อ */}
                  <td className="cmru-att-name">{show("nick") ? "" : "มาเรียน %"}</td>
                  {show("nick") && <td className="cmru-att-nick cmru-att-lock">มาเรียน %</td>}
                  {show("bonus") && <td className="cmru-att-nick" />}
                  {show("weeks") && weeks.map((weekNo, index) => (
                    <td key={weekNo} className={`cmru-att-week ${weekRates[index] === null ? "" : `is-${getRateTone(weekRates[index])}`}`} title={weekRates[index] === null ? `สัปดาห์ที่ ${weekNo}: ยังไม่ได้เช็คชื่อ` : `สัปดาห์ที่ ${weekNo}: มา + สาย ${weekRates[index]}% ของ ${weekTotals[index]} คนที่เช็คแล้ว`}>
                      {weekRates[index] === null ? "" : weekRates[index]}
                    </td>
                  ))}
                  {show("score") && <td className="cmru-att-score" />}
                  {show("status") && <td className="cmru-att-status" />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {isDirty && (
        <div className="cmru-unsaved" role="status" aria-live="polite">
          <div className="cmru-unsaved-text">
            <i className="fa-solid fa-triangle-exclamation" />
            <div>
              <strong>ยังไม่ได้บันทึกข้อมูล</strong>
              <span>
                เปลี่ยนแปลง {[Object.keys(draft).length ? `เช็คชื่อ ${Object.keys(draft).length} ช่อง` : "", Object.keys(statusDraft).length ? `สถานะ ${Object.keys(statusDraft).length} คน` : ""].filter(Boolean).join(" · ")} · กดบันทึกเพื่อเก็บลงระบบ
              </span>
            </div>
          </div>
          <div className="cmru-unsaved-actions">
            <button type="button" className="btn stg-btn stg-btn--ghost" onClick={handleCancel} disabled={isSaving}>ยกเลิก</button>
            <button type="button" className="btn stg-btn stg-btn--primary" onClick={handleSave} disabled={isSaving}>
              <i className="fa-solid fa-floppy-disk" />บันทึก
            </button>
          </div>
        </div>
      )}

      <SettingsModal show={showSettings} onHide={() => setShowSettings(false)} detail={detail} onSave={saveSettings} />
      <StatusModal
        show={!!statusTarget}
        onHide={() => setStatusTarget(null)}
        enrollment={statusTarget}
        autoStatusKey={statusTarget ? resolveEnrollmentStatus({ ...statusTarget, status_override: null }, points, threshold).key : "normal"}
        onSelect={stageStatus}
      />
    </div>
  );
}
